/**
 * "Jobs" gadget (Operate): every scheduled job of the services (the app configuration, shared with Configuration health
 * and Open work): when it runs next, how it went last, the failures, and Run now for an editor (vfs-service
 * `POST /app-config/services/:svc/jobs/:name/run`, as Operate › Jobs does). A viewer sees the list only.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { runJob } from '../../../services/AppConfigService';
import { RUN_LABEL, relative } from '../../appconfig/appConfigModel';
import { failedJobs, jobRows } from '../../../utils/homeGadgets/operate';
import GadgetCard, { GadgetBody, TabLink } from './GadgetCard';
import { useAppConfig } from './sources';

const SHOWN = 5;
const BAD = new Set(['failed', 'timeout']);

export default function JobsGadget({ projectId, canEdit = false, onOpenJobs = null }) {
  const { data, error, loading, refresh } = useAppConfig(projectId);
  const rows = jobRows(data?.details || {});
  const failures = failedJobs(rows);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const run = async(row) => {
    setBusy(`${row.service}/${row.name}`);
    setMessage(null);
    try {
      await runJob(projectId, row.service, row.name);
      setMessage({ ok: true, text: `${row.name} started.` });
      refresh();
    } catch (err) {
      setMessage({ ok: false, text: err.message || `${row.name} could not start.` });
    } finally {
      setBusy(null);
    }
  };
  return (
    <GadgetCard links={<TabLink onClick={onOpenJobs}>Operate › Jobs</TabLink>} meta={failures > 0 ? <span className="gd-bad">{failures} failed</span> : null} testId="jobs-gadget" title="Jobs">
      <GadgetBody
        data={data}
        empty={rows.length === 0}
        emptyText="No scheduled job yet."
        error={error}
        errorText="The jobs could not be read just now."
        loading={loading}
        loadingText="Reading the jobs…"
      >
        {() => (
          <>
            <ul className="gd-rows">
              {rows.slice(0, SHOWN).map((row) => (
                <li className="gd-row" data-testid={`job-${row.service}-${row.name}`} key={`${row.service}/${row.name}`}>
                  <strong>{row.name}</strong>
                  <span className="gd-grow ph-muted">
                    {row.service} · {row.enabled ? (row.nextRunAt ? `next ${relative(row.nextRunAt)}` : row.describe) : 'Paused'}
                    {row.lastRun && <> · last <span className={BAD.has(row.lastRun.status) ? 'gd-bad' : ''}>{RUN_LABEL[row.lastRun.status] || row.lastRun.status}</span></>}
                  </span>
                  {canEdit && !row.running && (
                    <button className="ph-link" disabled={busy === `${row.service}/${row.name}`} onClick={() => run(row)} type="button">Run now</button>
                  )}
                  {row.running && <span className="ph-muted ph-small">Running…</span>}
                </li>
              ))}
            </ul>
            {rows.length > SHOWN && <p className="gd-note">and {rows.length - SHOWN} more in Jobs.</p>}
            {data?.overview?.jobsNote && <p className="gd-note">{data.overview.jobsNote}</p>}
            {message && <p className={message.ok ? 'gd-note' : 'gd-error'} role={message.ok ? 'status' : 'alert'}>{message.text}</p>}
          </>
        )}
      </GadgetBody>
    </GadgetCard>
  );
}

JobsGadget.propTypes = { canEdit: PropTypes.bool, onOpenJobs: PropTypes.func, projectId: PropTypes.string.isRequired };
